import React from 'react';
import { Card, Field, TextField, SaveButton, useSection, ColorInput } from '../AdminUI';
import { Switch } from '../../ui/switch';
import { Plus, Trash2, ChevronUp, ChevronDown, Flame } from 'lucide-react';
import MediaUpload from '../MediaUpload';
import { defaultSiteData } from '../../../mock';
import { useSite } from '../../../context/SiteContext';

export default function ExchangeListingsSection() {
  const [raw, setLocal, save] = useSection('exchangeListings');
  const { updateSection } = useSite();
  const defaultEx = defaultSiteData.exchangeListings || {};

  const local = (raw && raw.types && raw.types.length >= 2 && raw.types[0].items?.length >= 5)
    ? raw
    : { ...defaultEx, ...(raw || {}), types: (raw?.types && raw.types.length >= 2 && raw.types[0].items?.length >= 5) ? raw.types : defaultEx.types };

  const set = (k, v) => {
    const next = { ...local, [k]: v };
    setLocal(next);
    updateSection('exchangeListings', next);
  };
  const setType = (i, patch) => set('types', (local.types || []).map((t, idx) => (idx === i ? { ...t, ...patch } : t)));
  const addType = () => set('types', [...(local.types || []), { id: `type_${Date.now()}`, label: 'B2C', items: [] }]);
  const rmType = (i) => set('types', (local.types || []).filter((_, idx) => idx !== i));
  const setItem = (ti, ii, patch) => setType(ti, { items: (local.types[ti]?.items || []).map((it, idx) => (idx === ii ? { ...it, ...patch } : it)) });
  const addItem = (ti) => setType(ti, { items: [...(local.types[ti]?.items || []), { id: `ex_${Date.now()}`, name: 'New Exchange', rate: '16%', type: local.types[ti]?.label || 'B2C', logo: '', link: '', description: '', popular: false }] });
  const rmItem = (ti, ii) => setType(ti, { items: (local.types[ti]?.items || []).filter((_, idx) => idx !== ii) });

  const moveItem = (ti, ii, dir) => {
    const items = [...(local.types[ti]?.items || [])];
    const targetIdx = dir === 'up' ? ii - 1 : ii + 1;
    if (targetIdx < 0 || targetIdx >= items.length) return;
    const temp = items[ii];
    items[ii] = items[targetIdx];
    items[targetIdx] = temp;
    setType(ti, { items });
  };

  // Collect all popular items across all groups for the Popular Section summary
  const popularItems = [];
  (local.types || []).forEach((typeGroup, ti) => {
    (typeGroup.items || []).forEach((item, ii) => {
      if (item.popular) {
        popularItems.push({ item, ti, ii, groupLabel: typeGroup.label });
      }
    });
  });

  return (
    <div data-testid="exchanges-section" className="space-y-6">
      {/* 1. Visibility Card */}
      <Card title="Section Visibility" desc="Turn the whole Exchange Listings section on/off on the site." footer={<SaveButton onClick={save} />}>
        <div className="flex items-center gap-3">
          <Switch checked={local.enabled !== false} onCheckedChange={(v) => set('enabled', v)} data-testid="exchanges-enabled-toggle" />
          <span className="text-sm font-semibold text-zinc-300">{local.enabled !== false ? 'Section is Visible on Website' : 'Section is Hidden'}</span>
        </div>
      </Card>

      {/* 2. DEDICATED POPULAR EXCHANGES MANAGEMENT SECTION */}
      <Card
        title="🔥 Popular Exchanges Section"
        desc="Dedicated section featuring popular exchanges. Toggle any exchange ON/OFF to feature it in the dedicated Popular section on the website."
        footer={<SaveButton onClick={save} />}
      >
        <div className="bg-amber-500/10 border border-amber-500/30 rounded-xl p-4 mb-4">
          <div className="flex items-center gap-2 text-amber-300 font-bold text-sm mb-1">
            <Flame size={18} className="fill-amber-400 text-amber-400" />
            <span>{popularItems.length} Platform(s) Featured as Popular</span>
          </div>
          <p className="text-xs text-zinc-300">
            Platforms marked as <strong>Popular</strong> get highlighted with glowing badges and are showcased in the dedicated <strong>Popular Exchanges</strong> section on the website home page.
          </p>
        </div>

        {popularItems.length === 0 ? (
          <div className="text-center py-6 text-zinc-400 text-xs border border-dashed border-zinc-800 rounded-lg">
            No platforms currently marked as Popular. Click "Popular" on any exchange listing below to feature it here!
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3">
            {popularItems.map(({ item, ti, ii, groupLabel }) => (
              <div key={item.id || `${ti}-${ii}`} className="flex items-center justify-between gap-2 p-3 bg-zinc-900 border border-amber-500/40 rounded-lg shadow-sm">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-1.5">
                    <span className="font-bold text-sm text-zinc-100 truncate">{item.name}</span>
                    <span className="text-[10px] px-1.5 py-0.5 rounded bg-amber-500/20 text-amber-300 font-extrabold border border-amber-500/30 uppercase">{groupLabel}</span>
                  </div>
                  <div className="text-xs text-zinc-400 truncate">{item.rate || '16%'} share rate</div>
                </div>
                <button
                  type="button"
                  onClick={() => setItem(ti, ii, { popular: false })}
                  className="px-2.5 py-1 rounded-md bg-amber-500/20 border border-amber-500/50 text-amber-300 hover:bg-amber-500/30 text-xs font-bold flex items-center gap-1 shrink-0 transition-all"
                  title="Remove from Popular section"
                >
                  <Flame size={12} className="fill-amber-300 text-amber-300" />
                  <span>Featured</span>
                </button>
              </div>
            ))}
          </div>
        )}
      </Card>

      {/* 3. SETTINGS & ALL GROUPS (B2C / B2B) */}
      <Card
        title="Exchange Listings Settings & Groups (B2C / B2B)"
        desc="Customize section headings, branding colors, and manage B2C and B2B exchange listings without layout overlapping."
        footer={<SaveButton onClick={save} />}
      >
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
          <Field label="Section Title"><TextField value={local.title || ''} onChange={(v) => set('title', v)} placeholder="EXCHANGES AVAILABLE" /></Field>
          <Field label="Subtitle"><TextField value={local.subtitle || ''} onChange={(v) => set('subtitle', v)} placeholder="All types of exchanges — IDs & white labels with live demo access" /></Field>
          <Field label="WhatsApp Number (for GET ID)" hint="With country code, e.g. 919876543210">
            <TextField value={local.whatsapp || ''} onChange={(v) => set('whatsapp', v)} />
          </Field>
          <Field label="Section Accent / Highlight Color">
            <ColorInput value={local.accentColor || '#ccc77d'} onChange={(v) => set('accentColor', v)} />
          </Field>
          <Field label="B2C Button & Badge Color">
            <ColorInput value={local.b2cColor || '#ccc77d'} onChange={(v) => set('b2cColor', v)} />
          </Field>
          <Field label="B2B Button & Badge Color">
            <ColorInput value={local.b2bColor || '#ccc77d'} onChange={(v) => set('b2bColor', v)} />
          </Field>
          <Field label="Popular Badge & Flame Color">
            <ColorInput value={local.popularColor || '#fbbf24'} onChange={(v) => set('popularColor', v)} />
          </Field>
          <Field label="Share Rate Badge Color">
            <ColorInput value={local.rateColor || '#ccc77d'} onChange={(v) => set('rateColor', v)} />
          </Field>
        </div>

        <div className="space-y-6">
          {(local.types || []).map((t, ti) => (
            <div key={t.id} className="rounded-xl border border-zinc-800 bg-zinc-900/60 p-4 md:p-5 space-y-4" data-testid={`exchange-type-${ti}`}>
              <div className="flex items-center justify-between gap-3 pb-3 border-b border-zinc-800">
                <div className="flex items-center gap-2 flex-1 max-w-sm">
                  <span className="text-xs font-bold text-zinc-400 uppercase tracking-wider">Group Name:</span>
                  <TextField value={t.label} onChange={(v) => setType(ti, { label: v })} placeholder="e.g. B2C or B2B" />
                </div>
                <button
                  type="button"
                  onClick={() => rmType(ti)}
                  className="px-3 py-1.5 rounded-lg bg-red-950/40 border border-red-900/50 text-red-400 hover:bg-red-900 hover:text-white text-xs font-bold flex items-center gap-1.5 transition-all shrink-0"
                  data-testid={`exchange-type-remove-${ti}`}
                >
                  <Trash2 size={14} /> Remove Group
                </button>
              </div>

              <div className="space-y-3">
                {(t.items || []).map((it, ii) => {
                  const isPopular = !!it.popular;
                  return (
                    <div
                      key={it.id || ii}
                      className="rounded-xl border border-zinc-800 bg-zinc-950/90 p-4 space-y-3 shadow-md hover:border-zinc-700 transition-colors"
                      data-testid={`exchange-item-${ti}-${ii}`}
                    >
                      {/* Top Bar: Title & Action Toolbar */}
                      <div className="flex flex-wrap items-center justify-between gap-2 pb-2 border-b border-zinc-900">
                        <div className="flex items-center gap-2 min-w-0">
                          <span className="w-2 h-2 rounded-full bg-[color:var(--gold)] shrink-0" />
                          <span className="font-bold text-sm text-zinc-100 truncate">{it.name || `Exchange #${ii + 1}`}</span>
                          <span className="text-xs px-2 py-0.5 rounded bg-zinc-900 border border-zinc-800 text-zinc-300 font-semibold">{it.rate || '16%'}</span>
                        </div>

                        {/* Action Buttons Toolbar - Clean, Non-overlapping */}
                        <div className="flex items-center gap-1.5 shrink-0 flex-wrap">
                          {/* Popular Toggle Switch */}
                          <button
                            type="button"
                            onClick={() => setItem(ti, ii, { popular: !isPopular })}
                            className={`h-8 px-2.5 rounded-lg border text-xs font-extrabold flex items-center gap-1.5 transition-all ${
                              isPopular
                                ? 'bg-amber-500/20 border-amber-500/60 text-amber-300 shadow-sm'
                                : 'bg-zinc-900 border-zinc-800 text-zinc-400 hover:text-zinc-200'
                            }`}
                            title="Toggle Popular Section Status"
                            data-testid={`exchange-item-popular-toggle-${ti}-${ii}`}
                          >
                            <Flame size={13} className={isPopular ? 'fill-amber-300 text-amber-300' : ''} />
                            <span>{isPopular ? 'Popular 🔥' : 'Normal'}</span>
                          </button>

                          {/* Move Up Button */}
                          <button
                            type="button"
                            onClick={() => moveItem(ti, ii, 'up')}
                            disabled={ii === 0}
                            title="Move Up"
                            className="w-8 h-8 rounded-lg bg-zinc-900 border border-zinc-800 text-zinc-300 hover:bg-zinc-800 disabled:opacity-20 disabled:cursor-not-allowed flex items-center justify-center shrink-0"
                            data-testid={`exchange-item-move-up-${ti}-${ii}`}
                          >
                            <ChevronUp size={15} />
                          </button>

                          {/* Move Down Button */}
                          <button
                            type="button"
                            onClick={() => moveItem(ti, ii, 'down')}
                            disabled={ii === (t.items || []).length - 1}
                            title="Move Down"
                            className="w-8 h-8 rounded-lg bg-zinc-900 border border-zinc-800 text-zinc-300 hover:bg-zinc-800 disabled:opacity-20 disabled:cursor-not-allowed flex items-center justify-center shrink-0"
                            data-testid={`exchange-item-move-down-${ti}-${ii}`}
                          >
                            <ChevronDown size={15} />
                          </button>

                          {/* Remove Button */}
                          <button
                            type="button"
                            onClick={() => rmItem(ti, ii)}
                            className="w-8 h-8 rounded-lg bg-red-950/40 border border-red-900/40 text-red-400 hover:bg-red-600 hover:text-white flex items-center justify-center shrink-0 transition-colors"
                            title="Remove Exchange"
                            data-testid={`exchange-item-remove-${ti}-${ii}`}
                          >
                            <Trash2 size={14} />
                          </button>
                        </div>
                      </div>

                      {/* Inputs Row 1: Name, Rate, Type, Official Link */}
                      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
                        <div>
                          <label className="block text-[11px] font-semibold text-zinc-400 mb-1">Platform Name</label>
                          <TextField value={it.name} onChange={(v) => setItem(ti, ii, { name: v })} placeholder="e.g. Lotus 1" />
                        </div>
                        <div>
                          <label className="block text-[11px] font-semibold text-zinc-400 mb-1">Share Rate</label>
                          <TextField value={it.rate} onChange={(v) => setItem(ti, ii, { rate: v })} placeholder="e.g. 16%" />
                        </div>
                        <div>
                          <label className="block text-[11px] font-semibold text-zinc-400 mb-1">Category Type</label>
                          <TextField value={it.type} onChange={(v) => setItem(ti, ii, { type: v })} placeholder="B2C or B2B" />
                        </div>
                        <div>
                          <label className="block text-[11px] font-semibold text-zinc-400 mb-1">Official Website Link</label>
                          <TextField value={it.link} onChange={(v) => setItem(ti, ii, { link: v })} placeholder="https://..." />
                        </div>
                      </div>

                      {/* Inputs Row 2: Subtitle/Tags & Logo Upload */}
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 items-end">
                        <div className="md:col-span-2">
                          <label className="block text-[11px] font-semibold text-zinc-400 mb-1">Tags / Subtitle Description</label>
                          <TextField
                            value={it.description || it.tags || ''}
                            onChange={(v) => setItem(ti, ii, { description: v, tags: v })}
                            placeholder="e.g. Live Cricket, Live Casino & Sportsbook"
                          />
                        </div>
                        <div>
                          <label className="block text-[11px] font-semibold text-zinc-400 mb-1">Platform Logo</label>
                          <MediaUpload label="Upload Logo" value={it.logo || ''} onChange={(v) => setItem(ti, ii, { logo: v })} />
                        </div>
                      </div>
                    </div>
                  );
                })}

                <button
                  onClick={() => addItem(ti)}
                  className="w-full h-10 rounded-xl border border-dashed border-zinc-700 hover:border-[color:var(--gold)]/60 text-zinc-300 text-xs font-bold flex items-center justify-center gap-2 bg-zinc-950/40 hover:bg-zinc-900/60 transition-all"
                  data-testid={`exchange-add-item-${ti}`}
                >
                  <Plus size={14} /> Add Exchange Platform to {t.label || 'Group'}
                </button>
              </div>
            </div>
          ))}

          <button
            onClick={addType}
            className="w-full h-11 rounded-xl border border-dashed border-amber-500/40 hover:border-amber-500 text-amber-300 text-sm font-bold flex items-center justify-center gap-2 bg-amber-500/5 hover:bg-amber-500/10 transition-all"
            data-testid="exchange-add-type"
          >
            <Plus size={16} /> Add New Group (e.g. B2C / B2B)
          </button>
        </div>
      </Card>
    </div>
  );
}
